import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { loadRecoveryCode, useAnonymousAuth } from '../auth';
import { useDistrict, useDistrictState } from '../district';
import { InstallPrompt } from '../shell';
import { db } from '../../lib/firebase';

/**
 * U0 入口（初参加 / 復帰選択）
 *
 * old2 `apps/user/src/pages/EntryPage.tsx` (93 行) を逐語移植。表示テキストは old2
 * `shared-i18n/ja/user.json#entry` を逐語ハードコード (v2 i18n は flat key で同 namespace
 * を保持していないため、CONSENT_TEXT 等と同方針で const 文言を直接埋め込む)。
 *
 * 当該地区のメンバーシップ (participants/{uid}/memberships/{districtId}) が存在し
 * consentAcceptedAt && nickname が立っていれば onboarding 済とみなす。
 * - 参加コード未発行なら recovery/issue に誘導
 * - 発行済なら本体 (map) へ
 *
 * v2 調整点:
 *   - 遷移先を old2 の `/app` → v2 主画面 `/map` に変更 (App.tsx の route 構造に合わせる)。
 *   - 「参加コード発行済」の判定: v2 `issueRecoveryCode` callable は recoveryCodes/{uid}
 *     (top-level) に書込み membership.recoveryCodeHash を更新しないため、membership の
 *     recoveryCodeHash に加えて localStorage の発行済コード (loadRecoveryCode) も
 *     「発行済」シグナルとして扱う (RecoveryIssuePage が persistRecoveryCode で保存する)。
 */

const ENTRY_TEXT = {
  title: 'ようこそ',
  newParticipant: '初めて参加する',
  returningParticipant: '以前参加したことがある（ユーザー復帰コードで復帰）',
} as const;

export function EntryPage() {
  const { urlToken } = useParams();
  const auth = useAnonymousAuth();
  const districtState = useDistrictState();
  const district = useDistrict();
  const base = `/m/${urlToken}`;

  const authLoading = auth.status === 'loading';
  const districtLoading =
    !districtState || districtState.status === 'loading' || districtState.status === 'idle';

  const [checking, setChecking] = useState(true);
  const [onboarded, setOnboarded] = useState(false);
  const [needsRecoveryCode, setNeedsRecoveryCode] = useState(false);

  const uid = auth.status === 'authenticated' ? auth.user.uid : null;
  // 地区フォールバック根絶: useDistrict() は ready のときのみ非 null。未解決は null。
  const districtId = district?.id ?? null;

  useEffect(() => {
    if (authLoading || districtLoading) return;
    if (!uid || !districtId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setChecking(false);
      return;
    }
    let cancelled = false;
    void getDoc(doc(db, 'participants', uid, 'memberships', districtId))
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data();
          if (d.consentAcceptedAt && d.nickname) {
            // 参加コードがまだ発行されていない場合は recovery/issue に誘導する。
            // v2: membership.recoveryCodeHash は issueRecoveryCode では更新されないため、
            // RecoveryIssuePage が localStorage に保存する発行済コードも併せて確認する。
            const hasLocalCode = loadRecoveryCode(districtId) !== null;
            if (!d.recoveryCodeHash && !hasLocalCode) {
              setNeedsRecoveryCode(true);
            } else {
              setOnboarded(true);
            }
          }
        }
      })
      .catch(() => {
        /* 取得失敗時は通常フローへ落とす */
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, districtLoading, uid, districtId]);

  if (authLoading || districtLoading || checking) {
    return <div className="p-8 text-center text-slate-500">読み込み中…</div>;
  }

  // 地区「強い停止」Phase 1: 停止中 (suspended:true) の短絡は DistrictLayout の DistrictGate が
  // 全ルート一括で行う (account-login を除き NonPublicScreen を表示)。EntryPage には到達しないため
  // ここでの分岐は不要。

  if (needsRecoveryCode) {
    return <Navigate to={`${base}/recovery/issue`} replace />;
  }

  if (onboarded) {
    return <Navigate to={`${base}/map`} replace />;
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <InstallPrompt />
      <h1 className="text-2xl font-bold text-center mt-8 mb-12">{ENTRY_TEXT.title}</h1>
      <div className="flex flex-col gap-4">
        <Link
          to={`${base}/onboarding`}
          className="bg-primary-600 text-white text-center py-4 rounded-md min-h-touch font-medium hover:bg-primary-700"
        >
          ✨ {ENTRY_TEXT.newParticipant}
        </Link>
        <Link
          to={`${base}/recovery/recover`}
          className="bg-slate-100 text-slate-900 text-center py-4 rounded-md min-h-touch font-medium hover:bg-slate-200"
        >
          🔑 {ENTRY_TEXT.returningParticipant}
        </Link>
      </div>
    </main>
  );
}
